// Client-side full-text search over the Aide registry. Content is already
// bundled as plain markdown strings (see index.ts), so this needs no backend
// — it's just string matching over data already in memory.
import type { AideTopic } from "./index";

export interface AideSearchResult {
  topic: AideTopic;
  snippet: string | null; // null when only the title matched
  titleMatch: boolean;
}

// Accent-insensitive, case-insensitive — French copy has enough accented
// characters ("épargne", "créances", "référent") that a literal match would
// miss a query typed without them.
function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

// Rough markdown-to-plain-text — good enough for search matching and for
// snippet display, not a full parser. Strips the syntax that would
// otherwise show up as noise in a snippet (##, **, |, etc.).
function stripMarkdown(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_~]{1,3}/g, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/\|/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

const SNIPPET_RADIUS = 70;

function buildSnippet(plainContent: string, normalizedContent: string, normalizedQuery: string): string {
  const index = normalizedContent.indexOf(normalizedQuery);
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(plainContent.length, index + normalizedQuery.length + SNIPPET_RADIUS);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < plainContent.length ? "…" : "";
  return `${prefix}${plainContent.slice(start, end).trim()}${suffix}`;
}

// Title matches rank above content-only matches; stable otherwise (registry
// order), which already reads top-to-bottom in a sensible module order.
export function searchAideTopics(topics: AideTopic[], rawQuery: string): AideSearchResult[] {
  const query = normalize(rawQuery.trim());
  if (!query) return [];

  const results: AideSearchResult[] = [];

  for (const topic of topics) {
    const titleMatch = normalize(topic.title).includes(query);

    let snippet: string | null = null;
    if (topic.content) {
      const plainContent = stripMarkdown(topic.content);
      const normalizedContent = normalize(plainContent);
      if (normalizedContent.includes(query)) {
        snippet = buildSnippet(plainContent, normalizedContent, query);
      }
    }

    if (titleMatch || snippet) {
      results.push({ topic, snippet, titleMatch });
    }
  }

  return results.sort((a, b) => Number(b.titleMatch) - Number(a.titleMatch));
}
